import { assetUrl } from './asset_url.js';
import { holdUntilReady } from './worker_ready.js';
import { app } from './app.js';
import { TIME_UNTIL_LOADING } from './constants.js';
import { isMatch } from './translations.js';
import { appSettings, updateSettingsFromUI } from './settings.js';
import { PIXEL_SCENE_SPAWN_DATA } from './pixel_scene_generation.js';
import { TRANSLATIONS } from './translations.js';
import { unlockedSpells } from './unlocks.js';
import { buildPixelSceneMetadata, getOrGenerateWorld } from './world_manager.js';
import { T10_SPELLS } from './spells.js';
import { GENERATOR_CONFIG } from './generator_config.js';
import { getMissingProgressSpells } from './progress.js';

const SEARCH_ENABLED = true;
let searchActive = false; // Whether to display the search results
let searchContinuing = false; // Whether the search is still ongoing
let pendingAutoNavigate = false; // Whether to go to the next match when found
let pendingForceNavigate = false; // Whether the deferred next match was requested by the user
let search = { results: [], index: -1, mode: null, autoNavigate: false };
export let activeLocalSearchArea = null;

let lastUpdateDrawTime = 0;
const UPDATE_DRAW_INTERVAL = 16; // ms

// Only applies to NG, since NG+ shuffles the biomes in a way that makes this less reasonable
// Plus the sort order usually only matters at the beginning of a run
const HOLY_MOUNTAIN_SORT_ANCHORS = [
    { y: 1024, afterBiome: 'coalmine_alt' },
    { y: 2560, afterBiome: 'excavationsite' },
    { y: 4608, afterBiome: 'snowcave' },
    { y: 6144, afterBiome: 'snowcastle' },
    { y: 8192, afterBiome: 'rainforest_open' },
    { y: 10240, afterBiome: 'vault' },
    { y: 12800, afterBiome: 'crypt' }
];

// Sync PoIs by PW with the worker so it can do filtering without needing to send all data back and forth
let syncedKeys = new Set();

export function syncSearchWorkerData() {
    searchWorker.postMessage({
        cmd: 'SYNC_METADATA',
        pixelSceneCache: buildPixelSceneMetadata(),
        pixelSceneSpawnDataCache: PIXEL_SCENE_SPAWN_DATA,
        translationsCache: TRANSLATIONS,
        unlockedSpellsCache: unlockedSpells,
        biomeData: app.biomeData,
        tileSpawns: app.tileSpawns
    });
    // Reset cache to ensure no weirdness with desyncs
    syncedKeys = new Set();
}

// Ensure you have an element id='search-background' in your HTML for this toggle
const isBackgroundSearchEnabled = () => document.getElementById('search-background')?.checked || false;
const autoIncrementSeedEnabled = () => document.getElementById('auto-increment-seed')?.checked || false;

// Soft cancel, without really canceling, but clearing highlights and resetting
export function clearHighlights() {
    for (let result of search.results) {
        // Clear the highlight from the REAL PoI
        // Doesn't apply to local search though
        if (result.pw !== undefined && result.pwVertical !== undefined && result.index !== undefined && app.poisByPW[`${result.pw},${result.pwVertical}`] && app.poisByPW[`${result.pw},${result.pwVertical}`][result.index]) {
            app.poisByPW[`${result.pw},${result.pwVertical}`][result.index].highlight = false;
        }
        //result.poi.highlight = false;
    }
	search.results = [];
	search.index = -1;
    search.mode = null;
    search.autoNavigate = true; // Reset to default for next search
    document.getElementById('search-nav').style.display = 'none';
    activeLocalSearchArea = null;
    // Might not be necessary?
    /*
	app.poisByPW[`${app.pw},${app.pwVertical}`]?.forEach(poi => {
		poi.highlight = false;
	});
    */
}

function getSearchFilters() {
    const searchInput = document.getElementById('search-input').value;
    const missingSpellSearch = /\b(missing|progress)\b/i.test(searchInput);
    const queryList = searchInput
        .split(',')
        .flatMap(query => {
            const normalizedQuery = query.trim().toLowerCase().replace('_', ' ');
            if (!/\b(missing|progress)\b/.test(normalizedQuery)) return normalizedQuery ? [normalizedQuery] : [];

            const remainingQuery = normalizedQuery.replace(/\b(missing|progress)\b/g, '').trim();
            const missingSpells = getMissingProgressSpells().map(spell => spell.toLowerCase().replace('_', ' '));
            return [...(remainingQuery ? [remainingQuery] : []), ...missingSpells];
        });
    if (queryList.length === 0 && missingSpellSearch) {
        queryList.push('__no_missing_progress_spells__');
    }

	return {
        queryList,
        missingSpellSearch,
		name: document.getElementById('search-name').value.toLowerCase(),
		sprite: document.getElementById('search-sprite').value,
		ac: document.getElementById('search-ac').value.toLowerCase(),
		acMode: document.getElementById('search-ac-mode').value,
		shuffleMode: document.getElementById('search-shuffle-mode').value,
		minSpells: parseInt(document.getElementById('spells-min-num').value),
		maxSpells: parseInt(document.getElementById('spells-max-num').value),
		minDelay: parseFloat(document.getElementById('delay-min-num').value),
		maxDelay: parseFloat(document.getElementById('delay-max-num').value),
		minRech: parseFloat(document.getElementById('rech-min-num').value),
		maxRech: parseFloat(document.getElementById('rech-max-num').value),
		minMana: parseInt(document.getElementById('mana-min-num').value),
		maxMana: parseInt(document.getElementById('mana-max-num').value),
		minManaRech: parseInt(document.getElementById('manarech-min-num').value),
		maxManaRech: parseInt(document.getElementById('manarech-max-num').value),
		minCap: parseInt(document.getElementById('cap-min-num').value),
		maxCap: parseInt(document.getElementById('cap-max-num').value),
		minSpread: parseInt(document.getElementById('spread-min-num').value),
		maxSpread: parseInt(document.getElementById('spread-max-num').value),
		minSpeed: parseFloat(document.getElementById('speed-min-num').value),
		maxSpeed: parseFloat(document.getElementById('speed-max-num').value),
		minLen: parseInt(document.getElementById('len-min-num').value),
		maxLen: parseInt(document.getElementById('len-max-num').value),
		minSpriteRarity: parseFloat(document.getElementById('rarity-min-num').value),
		maxSpriteRarity: parseFloat(document.getElementById('rarity-max-num').value),
		minTierIdx: parseInt(document.getElementById('tier-min-range').value),
		maxTierIdx: parseInt(document.getElementById('tier-max-range').value),
	};
}

const searchWorker = holdUntilReady(new Worker(assetUrl(new URL('./search_worker.js', new URL("../js/search_manager.js",import.meta.url).href)), { type: 'module', name: 'search' }));

searchWorker.onmessage = async (e) => {
    const msg = e.data;
    const bgMode = isBackgroundSearchEnabled();

    if (msg.type === 'STATUS' && searchActive) {
        if (!bgMode) {
            app.setLoading(true, msg.msg);
        }
        document.getElementById('search-status-container').style.display = 'flex';
        document.getElementById('search-status').innerText = msg.msg;
    }
    else if (msg.type === 'REQUEST_PW_DATA') {
        getOrGenerateWorld(msg.pw, msg.pwVertical);
    }
    else if (msg.type === 'MATCH_FOUND') {
        const { item, x, y } = msg;
        app.setLoading(false);
        app.extraPois = (app.extraPois || []).concat(item);
        search.results.push({ poi: item, x, y });
        updateUIForMatches();
    }
    else if (msg.type === 'LOCAL_MATCHES_FOUND') {
        const { matches } = msg;
        app.setLoading(false);
        app.extraPois = (app.extraPois || []).concat(matches.map(m => m.item));
        search.results.push(...matches.map(m => ({ poi: m.item, x: m.x, y: m.y })));
        updateUIForMatches();
    }
    else if (msg.type === 'MATCHES_FOUND') {
        const { matches, pw, pwVertical } = msg;
        app.setLoading(false);
        processPWMatches(matches, pw, pwVertical);
    }
    else if (msg.type === 'DONE') {
        app.setLoading(false);
        if (search.results.length === 0) {
            // Already done in update ui for matches
            /*
            let noResultsDisplay = 'No results found.';
            if (search.mode === 'pw' && search.pwSequence.length > 1) {
                noResultsDisplay = `No results found in this range of PWs.`;
            }
            else if (search.mode === 'pw') {
                noResultsDisplay = `No results found in this PW.`;
            }
            document.getElementById('search-results').innerHTML = `<div style="padding:5px; color:#888;">${noResultsDisplay}</div>`;
            cancelSearch();
            */

            // QoL: If the search was for something unlikely to occur, make a suggestion
            if (search.mode === 'pw' && search.pwSequence.length === 1 && appSettings.enableHamisHints) {
                if (msg.searchTarget) {
                    if (msg.searchTarget === 't10_spell') {
                        document.getElementById('search-all-pw').focus();
                        const choice = confirm("Hämis says: T10 spells are more likely to be found in vertical PWs. Would you like to search there?");
                        if (choice) {
                            document.getElementById('search-all-pw').checked = true;
                            document.getElementById('search-vertical-pw').checked = true;
                            document.getElementById('search-vertical-pw').focus();
                        }
                    }
                    else if (msg.searchTarget === 'true_orb') {
                        document.getElementById('local-search-mode').focus();
                        const choice = confirm("Hämis says: Orbs are more likely to be found in EoE local search mode. Would you like to switch?");
                        if (choice) {
                            document.getElementById('local-search-mode').value = 'eoe';
                            const event = new Event('change');
                            document.getElementById('local-search-mode').dispatchEvent(event);
                        }
                    }
                    else if (msg.searchTarget === 'sampo') {
                        document.getElementById('local-search-mode').focus();
                        const choice = confirm("Hämis says: Sampos are more likely to be found in EoE local search mode. Would you like to switch?");
                        if (choice) {
                            document.getElementById('local-search-mode').value = 'eoe';
                            const event = new Event('change');
                            document.getElementById('local-search-mode').dispatchEvent(event);
                        }
                    }
                }
            }
        }
        document.getElementById('search-status').innerText = '';
        document.getElementById('search-status-container').style.display = 'none';
        searchContinuing = false;
        pendingAutoNavigate = false;
        updateUIForMatches();
        // Do auto-increment if no matches were found
        if (search.results.length === 0 && autoIncrementSeedEnabled() && search.mode === 'pw') {
            const seedsRemaining = await app.incrementSeed();
            if (seedsRemaining) {
                performSearch(true, true);
            }
        }
    }
    else if (msg.type === 'PROGRESS') {
        // Update the area state
        activeLocalSearchArea = {
            x: msg.startX,
            y: msg.startY,
            r: msg.radius
        };
        
        // Redraw but not too frequently to avoid performance issues
        const now = performance.now();
        if (now - lastUpdateDrawTime > UPDATE_DRAW_INTERVAL) {
            app.draw();
            lastUpdateDrawTime = now;
        }
    }
};

async function updateUIForMatches() {
    document.getElementById('search-nav').style.display = 'block';
    document.getElementById('search-results').innerHTML = '';
    
    const cancelBtn = document.getElementById('cancel-search');
    cancelBtn.style.display = 'block';
    cancelBtn.innerText = searchContinuing ? "Stop Search" : "Clear Results";

    if (search.results.length === 0) {
        let noResultsDisplay = 'No results found.';
        if (search.mode === 'pw' && search.pwSequence.length > 1) {
            noResultsDisplay = `No results found in this range of PWs.`;
        }
        else if (search.mode === 'pw') {
            noResultsDisplay = `No results found in this PW.`;
        }
        document.getElementById('search-results').innerHTML = `<div style="padding:5px; color:#888;">${noResultsDisplay}</div>`;
        cancelBtn.style.display = 'none';
        document.getElementById('search-nav').style.display = 'none';
        // Cancel search?
    }
    else {
        // Highlight PoIs
        search.results.forEach(result => {
            result.highlight = true;
        });
        // Deferred next-result requests must advance immediately. Initial results only do so when automatic navigation is enabled.
        if (pendingAutoNavigate || (search.index === -1 && search.autoNavigate)) {
            const forceNavigate = pendingAutoNavigate && pendingForceNavigate;
            pendingAutoNavigate = false;
            pendingForceNavigate = false;
            await navigateSearch(1, forceNavigate);
        } else {
            const suffix = searchContinuing ? "..." : "";
            document.getElementById('search-count').innerText = `${search.index + 1} / ${search.results.length}${suffix}`;
        }
    }

    if (!searchContinuing) app.setLoading(false);
}

export async function performSearch(allowIterative = true, autoNavigate = true) {
    if (!SEARCH_ENABLED) return;
    if (searchActive && allowIterative) return;
    
    const searchAllPW = allowIterative && document.getElementById('search-all-pw').checked;
    // Default value of 6 here was causing issues with trying to search only verticals with limit 0
    const pwLimit = parseInt(document.getElementById('search-pw-limit').value);
    const searchVerticalPW = document.getElementById('search-vertical-pw').checked;
    const pwVerticalLimit = parseInt(document.getElementById('search-pw-vertical-limit').value);
    const cancelBtn = document.getElementById('cancel-search');
    const excludeNegativeVerticals = searchVerticalPW && (document.getElementById('exclude-negative-verticals')?.checked || false);

    clearHighlights();
    document.getElementById('search-nav').style.display = 'none';
    document.getElementById('search-results').innerHTML = '';

    searchActive = true;
    search.results = [];
    search.index = -1;
    search.mode = 'pw';
    search.autoNavigate = autoNavigate;
    
    const filters = getSearchFilters();
    // Do some smarter filtering based on the type of search
    // First determine if it's a wand search
    let searchTarget = null;
    if (filters.queryList.length === 0) {
        searchTarget = 'wand';
    }
    else if (filters.queryList.length === 1) {
        const query = filters.queryList[0];
        if (isMatch('true_orb', query)) {
            searchTarget = 'true_orb';
        }
        else if (isMatch('sampo', query)) {
            searchTarget = 'sampo';
        }
        else {
            // Check if it's a tier 10 spell
            for (let spellName of T10_SPELLS) {
                if (isMatch(spellName, query)) {
                    searchTarget = 't10_spell';
                    //console.log(`Identified search for tier 10 spell: ${spellName}`);
                    // Automatically override settings if it's unlikely to find anything maybe?
                    // Eh maybe only if it fails
                    break;
                }
            }
        }
    }
    if (!searchTarget) searchTarget = 'other';

    let currentSequence;

    // Generate the standard PW Sequence
    if (!searchVerticalPW) {
        currentSequence = ['0,0'];
        for (let i = 1; i <= pwLimit; i++) { 
            currentSequence.push(`${i},0`); 
            currentSequence.push(`-${i},0`); 
        }
    } else {
        const coords = [];
        // Generate all valid coordinates within the rectangle
        for (let x = -pwLimit; x <= pwLimit; x++) {
            for (let y = -pwVerticalLimit; y <= pwVerticalLimit; y++) {
                if (y < 0 && ((excludeNegativeVerticals && searchAllPW) || ((searchTarget === 'wand' || searchTarget === 'other') && app.gameMode !== 'nightmare'))) continue; // Skip negative vertical PWs for wand searches since they can't spawn there
                // I would also exclude the other vertical PWs except that the infinite power plant exists
                coords.push({ x, y, dist: Math.abs(x) + Math.abs(y) });
            }
        }
        // Sort by Manhattan distance, then by X and Y to keep it deterministic
        coords.sort((a, b) => a.dist - b.dist || a.x - b.x || a.y - b.y);
        currentSequence = coords.map(c => `${c.x},${c.y}`);
    }

    // Logic for Manual Search
    if (!searchAllPW) {
        // If we are only searching the current PW, we don't need the whole sequence
        currentSequence = [`${app.pw},${app.pwVertical}`];
    } else {
        // If searching all, ensure the starting PW is in the sequence if it falls outside the limit
        if (!currentSequence.includes(`${app.pw},${app.pwVertical}`)) {
            currentSequence.push(`${app.pw},${app.pwVertical}`);
        }
        
        cancelBtn.style.display = 'block';
        cancelBtn.innerText = "Cancel Search";
    }

    search.pwSequence = currentSequence;

    // Give a small yield for the setLoading timer to start
    if (searchAllPW && !isBackgroundSearchEnabled()) {
        app.setLoading(true, "Initializing Search...");
        await new Promise(r => setTimeout(r, TIME_UNTIL_LOADING));
    }

    searchContinuing = isBackgroundSearchEnabled() || searchAllPW; // If searching all PW, we consider it a long-running search even if not in background mode

    // Rare-ish search pre-checks
    if (isMatch('vault_puzzle_varpuluuta', filters.queryList[0])) {
        // Check tiles for possible broom spawns to avoid wasting time searching a seed that can't spawn them
        let seedValid = false;
        app.tileLayers.forEach(layer => {
            if (layer.biomeName != 'vault') return;
            // Cheap check of tile indices
            layer.tileIndices.forEach(tileIndex => {
                if (tileIndex == 10 || tileIndex == 14) {
                    seedValid = true;
                    return;
                }
            });
            // More expensive but precise check of spawn pixel colors
            // 0xFF0080
            if (seedValid) {
                seedValid = false;
                for (let x = 0; x < layer.width; x++) {
                    for (let y = 0; y < layer.mapH; y++) {
                        const pixelIndex = y * layer.width + x;
                        const pixelColor = layer.buffer[pixelIndex+2] | (layer.buffer[pixelIndex+1] << 8) | (layer.buffer[pixelIndex] << 16);
                        if (pixelColor == 0xFF0080) {
                            seedValid = true;
                            break;
                        }
                    }
                    if (seedValid) break;
                }
            }
        });
        if (!seedValid) {
            alert("Hämis says: This seed does not have any possible broom spawns!");
            cancelSearch();
            return;
        }
    }

    if (isMatch('meditation_cube', filters.queryList[0])) {
        let seedValid = false;
        app.tileLayers.forEach(layer => {
            if (layer.biomeName != 'excavationsite') return;
            // Cheap check of tile indices
            layer.tileIndices.forEach(tileIndex => {
                if (tileIndex == 4 || tileIndex == 12 || tileIndex == 16384+17 || tileIndex == 16384+23 || tileIndex == 16384+25) {
                    seedValid = true;
                    return;
                }
            });
            // More expensive but precise check of spawn pixel colors
            // 0xb09016
            if (seedValid) {
                seedValid = false;
                for (let x = 0; x < layer.width; x++) {
                    for (let y = 0; y < layer.mapH; y++) {
                        const pixelIndex = y * layer.width + x;
                        const pixelColor = layer.buffer[pixelIndex+2] | (layer.buffer[pixelIndex+1] << 8) | (layer.buffer[pixelIndex] << 16);
                        if (pixelColor == 0xb09016) {
                            seedValid = true;
                            break;
                        }
                    }
                    if (seedValid) break;
                }
            }
        });
        if (!seedValid) {
            alert("Hämis says: This seed does not have any possible meditation cube spawns!");
            cancelSearch();
            return;
        }
    }

    if (isMatch('buried_eye_teleporter', filters.queryList[0])) {
        let seedValid = false;
        app.tileLayers.forEach(layer => {
            if (layer.biomeName != 'snowcave') return;
            // Cheap check of tile indices
            layer.tileIndices.forEach(tileIndex => {
                if (tileIndex == 15 || tileIndex == 23 || tileIndex == 16384+31) {
                    seedValid = true;
                    return;
                }
            });
            // More expensive but precise check of spawn pixel colors
            // 0x00AC33
            if (seedValid) {
                seedValid = false;
                for (let x = 0; x < layer.width; x++) {
                    for (let y = 0; y < layer.mapH; y++) {
                        const pixelIndex = y * layer.width + x;
                        const pixelColor = layer.buffer[pixelIndex+2] | (layer.buffer[pixelIndex+1] << 8) | (layer.buffer[pixelIndex] << 16);
                        if (pixelColor == 0x00AC33) {
                            seedValid = true;
                            break;
                        }
                    }
                    if (seedValid) break;
                }
            }
        });
        if (!seedValid) {
            alert("Hämis says: This seed does not have any possible buried eye spawns!");
            cancelSearch();
            return;
        }
    }

    // Send the payload to the worker
    searchWorker.postMessage({
        cmd: 'START_PW_SEARCH',
        type: 'pw',
        backgroundMode: isBackgroundSearchEnabled(),
        filters: filters,
        pwSequence: search.pwSequence,
        searchTarget: searchTarget,
        //seed: app.seed,
        //ngPlusCount: app.ngPlusCount,
        //perks: app.perks,
        //skipCosmeticScenes: app.skipCosmeticScenes,
        //poisByPW: app.poisByPW // expensive
    });
}

export async function performLocalSearch(mode, startX, startY) {
    if (!SEARCH_ENABLED) return;
    if (searchActive) cancelSearch(); 

    clearHighlights();
    document.getElementById('search-nav').style.display = 'none';
    document.getElementById('search-results').innerHTML = '';

    searchActive = true;
    search.results = [];
    search.index = -1;
    search.mode = 'local';
    search.autoNavigate = true; // Doesn't really mean the same thing

    const cancelBtn = document.getElementById('cancel-search');
    cancelBtn.style.display = 'block';
    cancelBtn.innerText = "Cancel Search";
    // Actually looks better without this display
    /*
    if (!isBackgroundSearchEnabled()) {
        app.setLoading(true, "Searching...");
    }
    */
    
    const filters = getSearchFilters();

    let quickSearch = null;
    if (mode === 'eoe' && filters.queryList.length === 1 && isMatch('true_orb', filters.queryList[0])) quickSearch = 'true_orb';
    else if (mode === 'eoe' && filters.queryList.length === 1 && isMatch('sampo', filters.queryList[0])) quickSearch = 'sampo';
    // No real speedup for this compared to highcap since we're already using set lookups, but I did precompute the seeds for these so might as well use them
    else if (filters.minSpells >= 27 && (mode === 'dragon' || mode === 'tiny')) quickSearch = 'highsc';
    else if (filters.minCap >= 27 || filters.minSpells >= 27) quickSearch = 'highcap';
    else if (filters.minSpriteRarity >= 7) quickSearch = 'raresprite';

    searchContinuing = true; //isBackgroundSearchEnabled();
    
    searchWorker.postMessage({
        cmd: 'START_LOCAL_SEARCH',
        type: 'local',
        backgroundMode: isBackgroundSearchEnabled(),
        mode,
        quickSearch,
        startX,
        startY,
        filters,
        seed: app.seed,
        ngPlusCount: app.ngPlusCount,
        perks: app.perks
    });
}

export async function navigateSearch(dir, forceNavigate = false) {
    if (search.results.length === 0) return;
    const bgMode = isBackgroundSearchEnabled();
    
    // If we are at the end of results, and NOT in background mode, ask the worker for the next one
    // If searchContinuing is false, but this was triggered, it means the search was re-enabled...
    if (dir === 1 && search.index === search.results.length - 1 && !bgMode && searchContinuing) {
        // Check if it's a local search, if so don't display the loading
        if (search.mode !== 'local') {
            app.setLoading(true, "Searching...");
        }
        pendingAutoNavigate = true;
        pendingForceNavigate = forceNavigate;
        searchWorker.postMessage({ cmd: 'FIND_NEXT' });
        return;
    }

    search.index += dir;
    if (search.index >= search.results.length) search.index = 0;
    if (search.index < 0) search.index = search.results.length - 1;

    const current = search.results[search.index];
    
    // PW map sync logic
    if (current.pw !== undefined && `${app.pw},${app.pwVertical}` !== `${current.pw},${current.pwVertical}`) {
        app.pw = current.pw;
        app.pwVertical = current.pwVertical;
        document.getElementById('pw').value = app.pw;
        document.getElementById('pw-vertical').value = app.pwVertical;
    }

    const suffix = searchContinuing ? " ..." : "";
    document.getElementById('search-count').innerText = `${search.index + 1} / ${search.results.length}${suffix}`;
    
    // Automatic navigation may be disabled after a PW transition, but button presses always navigate.
    if (search.autoNavigate || forceNavigate) {
        app.gotoPOI(current.poi);
    }
    else {
        // Ensure the PoIs are still rendered
        app.draw();
    }
}

export function cancelSearch() {
    searchActive = false;
    activeLocalSearchArea = null;
    pendingAutoNavigate = false;
    pendingForceNavigate = false;
    document.getElementById('search-status').innerText = '';
    document.getElementById('search-status-container').style.display = 'none';
    searchWorker.postMessage({ cmd: 'CANCEL' }); 
    // Not clearing highlights here
    const cancelBtn = document.getElementById('cancel-search');
    // TODO: Doing this based on the text is not ideal
    if (searchContinuing) {
        //console.log("Stopping search but keeping results...");
        cancelBtn.innerText = 'Clear Results';
        cancelBtn.style.display = 'block';
        searchContinuing = false;
        document.getElementById('search-count').innerText = `${search.index + 1} / ${search.results.length}`;
    }
    else {
        cancelBtn.style.display = 'none';
        document.getElementById('search-nav').style.display = 'none';
        clearHighlights();
    }
    
    // Keep searchNav visible if we have results
    /*
    if (search.results.length === 0) {
        const searchNav = document.getElementById('search-nav');
        if (searchNav) searchNav.style.display = 'none';
    }
    */
}

export function isSearchActive() {
    return searchActive;
}

// TODO: Sync...
export function syncSettingsToSearchWorker() {
    updateSettingsFromUI();
    searchWorker.postMessage({
        cmd: 'SYNC_SETTINGS',
        settings: appSettings,
        unlockedSpellsCache: unlockedSpells
    });
    //console.log(appSettings);
    // Reset cache to ensure no weirdness with desyncs
    syncedKeys = new Set();
}

function processPWMatches(matches, pw, pwVertical) {
    for (let match of matches) {
        // Redundant
        const pwKey = `${match.pw},${match.pwVertical}`;
        
        // Ensure the array exists and the index is valid
        if (app.poisByPW[pwKey] && app.poisByPW[pwKey][match.index]) {
            // 1. Grab the actual, authoritative object the map is using to render
            const realPoi = app.poisByPW[pwKey][match.index];
            
            // 2. Turn on the highlight for the map renderer
            realPoi.highlight = true;
            
            // 3. Swap out the cloned worker object for the real one 
            // so app.gotoPOI() navigates to the correct reference later
            match.poi = realPoi;
        }
    }

    // Sort the matches by biome order, then by Y. Needs an exception for holy mountains
    // This only needs to be done for the main world
    // This is going to be broken for nightmare but whatever
    if (!app.isNGP && pw === 0 && pwVertical === 0) {
        const biomeOrder = new Map(Object.keys(GENERATOR_CONFIG).map((biome, index) => [biome, index]));
        const getBiomeSortOrder = (poi) => {
            if (!poi.biome?.includes('temple_altar')) {
                return biomeOrder.get(poi.biome) ?? Number.MAX_SAFE_INTEGER;
            }

            const anchor = HOLY_MOUNTAIN_SORT_ANCHORS.reduce((closest, candidate) =>
                Math.abs(candidate.y - poi.y) < Math.abs(closest.y - poi.y) ? candidate : closest
            );
            return (biomeOrder.get(anchor.afterBiome) ?? Number.MAX_SAFE_INTEGER) + 0.5;
        };
        matches.sort((a, b) => {
            const aBiomeIndex = getBiomeSortOrder(a.poi);
            const bBiomeIndex = getBiomeSortOrder(b.poi);
            return aBiomeIndex - bBiomeIndex || a.poi.y - b.poi.y || a.poi.x - b.poi.x;
        });
    }

    // Now safely add them to the search results
    search.results.push(...matches);
    app.draw();
    updateUIForMatches();
}

// TODO: Refactor to not repeat the world generation here
export function continueSearchSequence(pw, pwVertical) {
    // Called by the world worker after generating a world
    const key = `${pw},${pwVertical}`;
    if (app.poisByPW[key] && !syncedKeys.has(key)) {
        syncedKeys.add(key);
        searchWorker.postMessage({
            cmd: 'SYNC_DATA',
            pw,
            pwVertical,
            pois: app.poisByPW[key]
        });
    }

    // Is this right?
    if (searchActive) {
        searchWorker.postMessage({
            cmd: 'FIND_NEXT'
        });
    }
}

// Testing only syncing (doesn't quite work)
export function syncPW(pw, pwVertical) {
    const key = `${pw},${pwVertical}`;
    if (app.poisByPW[key]) {
        syncedKeys.add(key);
        searchWorker.postMessage({
            cmd: 'SYNC_DATA',
            pw,
            pwVertical,
            pois: app.poisByPW[key]
        });
    }
}